import { Link } from "react-router-dom";
import { VeyaLayout, Section, H2, SampleConversation, EarlyAccessForm, CheckCard, Reveal, SOURCES, SOURCES_CHECKED, useMeta } from "./VeyaShared";

const PRODUCTS = ["veya by qwip", "Romie by Expedia", "Mindtrip", "Poke", "Instinct"];

const ROWS: { label: string; cells: string[] }[] = [
  { label: "Main focus", cells: [
    "Checking whether bookings you already have fit together",
    "Travel planning, shopping and booking assistant inside Expedia",
    "Trip inspiration, itinerary planning and booking",
    "General personal assistant over messaging (email, calendar, reminders)",
    "General personal assistant that can carry out tasks for you",
  ]},
  { label: "Starting point", cells: [
    "An existing trip",
    "A trip idea or an Expedia booking",
    "A trip idea, destination or saved places",
    "A general assistant request",
    "A general assistant request",
  ]},
  { label: "Brings trip details together", cells: [
    "Planned focus",
    "Announced in 2024; not publicly confirmed today",
    "Yes - upload or forward booking confirmations",
    "Not publicly confirmed",
    "Not publicly confirmed",
  ]},
  { label: "Checks for gaps or fragile connections between bookings", cells: [
    "Planned focus",
    "Not publicly confirmed",
    "Not publicly confirmed",
    "Not publicly confirmed",
    "Not publicly confirmed",
  ]},
  { label: "Helps plan, recommend or book", cells: [
    "Not the focus; may suggest options to look at",
    "Announced to plan, shop and book",
    "Yes - recommendations, itineraries and booking",
    "General tasks; travel not its stated focus",
    "Lists planning a weekend getaway among example tasks",
  ]},
  { label: "Monitors for changes that may affect the trip", cells: [
    "In development",
    "Announced to help when plans change; not publicly confirmed today",
    "Not publicly confirmed",
    "Proactive updates from connected services",
    "Not publicly confirmed",
  ]},
  { label: "How people use it", cells: [
    "By messaging (format being explored)",
    "Was part of the Expedia app; reported discontinued in 2026",
    "Web and iOS app",
    "Messages, WhatsApp, Telegram and RCS",
    "By text or phone call",
  ]},
];

const veya = () => {
  useMeta(
    "veya by qwip - check your booked trip fits together",
    "veya brings your flight, stay and transport details together and points out the bits worth checking, like tight connections or late arrivals.",
  );

  return (
    <VeyaLayout>
      {/* Hero */}
      <Section className="grid grid-cols-2 items-center gap-16 pt-16 mobile:grid-cols-1 mobile:gap-12 tablet:grid-cols-1">
        <Reveal>
          <p className="text-sm font-medium uppercase tracking-widest text-muted-foreground">veya by qwip · In development</p>
          <h1 className="mt-5 font-funnel text-6xl font-bold leading-[1.02] tracking-tight mobile:text-[2.6rem]">
            Your trip looks booked. <span className="font-cormorant font-normal italic">Does it all line up?</span>
          </h1>
          <p className="mt-6 max-w-xl text-lg text-muted-foreground">
            veya brings your flight, stay and transport details together, then points out the bits worth checking - a tight connection, no airport transfer, or a late arrival before hotel check-in.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-5">
            <a href="#early-access" className="rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl">Get early access</a>
            <Link to="/veya-how-it-works" className="border-b border-primary/20 pb-0.5 text-sm font-medium transition-colors hover:border-primary">How it works</Link>
          </div>
        </Reveal>
        <SampleConversation />
      </Section>

      {/* Problem */}
      <div className="px-3 mobile:px-2">
        <Section className="rounded-[3rem] bg-muted/40 mobile:rounded-[2rem]">
          <H2>The awkward bits are usually between bookings.</H2>
          <p className="mt-5 max-w-2xl text-lg text-muted-foreground">
            Most trips are booked across a few different sites. Each booking looks fine on its own. The trouble shows up when you line them up.
          </p>
          <Reveal className="mt-12 grid grid-cols-3 gap-6 mobile:grid-cols-1 tablet:grid-cols-1">
            {[
              ["Late arrival", "Your flight lands at 23:10. The guesthouse reception closes at 23:00."],
              ["Tight connection", "Seventy minutes to change terminals, on two separate tickets."],
              ["No way there", "You've booked the flight and the flat, but nothing to get from the airport to the door."],
            ].map(([t, d]) => (
              <div key={t} className="rounded-3xl bg-card p-8 shadow-[0_4px_24px_-10px_rgba(31,61,50,0.12)] transition-all duration-300 hover:-translate-y-1.5 hover:shadow-[0_20px_40px_-16px_rgba(31,61,50,0.2)]">
                <h3 className="font-funnel text-xl font-bold">{t}</h3>
                <p className="mt-2 text-muted-foreground">{d}</p>
              </div>
            ))}
          </Reveal>
        </Section>
      </div>

      {/* How it works */}
      <Section id="how-it-works">
        <H2>How it works</H2>
        <Reveal className="mt-16 grid grid-cols-3 gap-10 mobile:grid-cols-1 tablet:grid-cols-1">
          <ol className="contents">
            {[
              ["Add your trip details", "Add or share booking confirmations and tell veya what it needs to know. Exactly how you'll share them is still being decided."],
              ["veya checks how the pieces fit", "It looks for missing details, timing issues and connections worth checking."],
              ["Get a heads-up if something changes", "veya can watch relevant travel updates and tell you what may need attention."],
            ].map(([t, d], i) => (
              <li key={t} className="relative">
                <span aria-hidden className="block font-funnel text-7xl font-bold leading-none text-primary/10">0{i + 1}</span>
                <h3 className="mt-4 font-funnel text-xl font-bold">{t}</h3>
                <p className="mt-2 text-muted-foreground">{d}</p>
              </li>
            ))}
          </ol>
        </Reveal>
        <Link to="/veya-how-it-works" className="mt-10 inline-block border-b border-primary/20 pb-0.5 text-sm font-medium transition-colors hover:border-primary">More on how it works</Link>
      </Section>

      {/* Checks */}
      <div className="px-3 mobile:px-2">
        <Section id="trip-checks" className="rounded-[3rem] bg-primary text-primary-foreground mobile:rounded-[2rem]">
          <H2>What veya checks</H2>
          <ul className="mt-10 grid grid-cols-2 gap-x-10 gap-y-4 text-lg mobile:grid-cols-1">
            {[
              "Flight and accommodation details",
              "Time between connected bookings",
              "Airport transfers and onward transport",
              "Missing essentials in the trip plan",
              "Flight or airport changes before departure",
              "Patterns in recent flight delays, where reliable data is available",
            ].map((c) => (
              <li key={c} className="flex gap-3 border-b border-white/15 pb-4"><span aria-hidden className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-foreground" />{c}</li>
            ))}
          </ul>
          <div id="monitoring" className="mt-14 scroll-mt-24">
            <h3 className="font-funnel text-2xl font-bold">What a monitoring note could look like</h3>
            <p className="mt-2 max-w-2xl text-primary-foreground/80">Each note says what veya saw, when it looked, and where the information came from. A pattern is a reason to plan ahead, not a prediction.</p>
            <Reveal className="mt-8 grid grid-cols-2 gap-6 mobile:grid-cols-1">
              <CheckCard title="Recent arrivals running late" observed="This flight number arrived late on 4 of the past 7 days." checked="3 days before departure" source="Published flight arrival records" />
              <CheckCard title="Departure terminal changed" observed="Your departure is now listed from a different terminal." checked="The morning before you fly" source="Airline flight status" />
            </Reveal>
          </div>
          <div className="mt-8 flex gap-6 text-sm">
            <Link to="/veya-trip-check" className="underline underline-offset-4">See trip check examples</Link>
            <Link to="/veya-monitoring" className="underline underline-offset-4">About monitoring</Link>
          </div>
        </Section>
      </div>

      {/* Comparison */}
      <Section id="comparison">
        <H2>What veya is for</H2>
        <p className="mt-5 max-w-2xl text-lg text-muted-foreground">
          Travel tools can help you plan, book or manage a trip. veya's focus is checking whether the bookings you already have fit together - and what might need attention before you leave.
        </p>

        <Reveal className="mt-12 overflow-x-auto rounded-3xl bg-card shadow-[0_8px_40px_-16px_rgba(31,61,50,0.18)] mobile:hidden">
          <table className="w-full min-w-[900px] text-left text-sm">
            <caption className="sr-only">Comparison of veya with other travel and assistant tools</caption>
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th scope="col" className="w-44 p-4 font-medium text-muted-foreground"><span className="sr-only">Feature</span></th>
                {PRODUCTS.map((p, i) => (
                  <th key={p} scope="col" className={`p-4 font-funnel text-base font-bold ${i === 0 ? "bg-primary text-primary-foreground" : ""}`}>{p}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r) => (
                <tr key={r.label} className="border-b border-border last:border-0 align-top">
                  <th scope="row" className="p-4 font-medium">{r.label}</th>
                  {r.cells.map((c, i) => (
                    <td key={i} className={`p-4 ${i === 0 ? "bg-primary/5 font-medium" : "text-muted-foreground"}`}>{c}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Reveal>

        <div className="mt-10 hidden space-y-4 mobile:block">
          {PRODUCTS.map((p, pi) => (
            <article key={p} className={`rounded-3xl bg-card p-5 shadow-[0_4px_24px_-10px_rgba(31,61,50,0.12)] ${pi === 0 ? "bg-primary/5" : ""}`}>
              <h3 className="font-funnel text-lg font-bold">{p}</h3>
              <dl className="mt-3 space-y-3 text-sm">
                {ROWS.map((r) => (
                  <div key={r.label}>
                    <dt className="font-medium">{r.label}</dt>
                    <dd className="text-muted-foreground">{r.cells[pi]}</dd>
                  </div>
                ))}
              </dl>
            </article>
          ))}
        </div>

        <div className="mt-6 text-xs text-muted-foreground">
          <p><span className="font-medium">Sources</span> (checked {SOURCES_CHECKED}): {SOURCES.map((s, i) => (
            <span key={s.url}><a href={s.url} target="_blank" rel="noreferrer" className="underline underline-offset-2">{s.name}</a>{i < SOURCES.length - 1 ? "; " : "."}</span>
          ))}</p>
          <p className="mt-2">Product names belong to their owners. Descriptions are based on public information and may have changed.</p>
        </div>
      </Section>

      {/* Early access */}
      <div className="px-3 mobile:px-2">
        <Section id="early-access" className="grid grid-cols-2 items-center gap-12 rounded-[3rem] bg-primary text-primary-foreground mobile:grid-cols-1 mobile:rounded-[2rem] tablet:grid-cols-1">
          <div>
            <h2 className="font-funnel text-4xl font-bold leading-tight mobile:text-3xl">Try veya before your next trip</h2>
            <p className="mt-4 text-primary-foreground/80">veya is in development. Leave your email and we'll let you know when there's an early version to try.</p>
          </div>
          <div className="rounded-[2rem] bg-background p-6 text-foreground shadow-[0_20px_50px_-20px_rgba(0,0,0,0.35)]">
            <EarlyAccessForm source="veya-landing" />
          </div>
        </Section>
      </div>

      {/* FAQ */}
      <Section id="faq">
        <H2>Questions</H2>
        <div className="mt-10 divide-y divide-border/60 border-y border-border/60">
          {LANDING_FAQ.map(([q, a]) => (
            <details key={q} className="group py-5">
              <summary className="cursor-pointer list-none font-funnel text-lg font-bold marker:hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <span className="flex items-center justify-between gap-4">{q}<span aria-hidden className="text-muted-foreground transition group-open:rotate-45">+</span></span>
              </summary>
              <p className="mt-3 max-w-3xl text-muted-foreground">{a}</p>
            </details>
          ))}
        </div>
        <Link to="/veya-faq" className="mt-8 inline-block text-sm underline underline-offset-4">All questions</Link>
      </Section>
    </VeyaLayout>
  );
};

export const LANDING_FAQ: [string, string][] = [
  ["What does veya check?", "How your flight, stay and transport details fit together: timing between bookings, missing transfers, late arrivals against check-in times, and other details worth confirming before you go."],
  ["Can I add bookings from different providers?", "That's the idea. Most trips are booked in a few places, so veya is being designed to work across them rather than with one booking site."],
  ["How do I share booking details?", "This is still being decided. We're exploring options such as sharing confirmations directly. We won't connect to your email or any account without asking you first."],
  ["Does veya book or change things for me?", "No. veya points out what may need attention and suggests options to look at. You stay in charge of booking and changes."],
  ["What can it monitor?", "We're exploring flight status, airport and terminal changes, and recent delay patterns where reliable data is available. Monitoring is in development."],
  ["What happens to my travel information?", "It would be used only to check and monitor your trip. Right now, the only thing we collect is the email you give us for early access."],
];

export default veya;
